<template>
  <div class="save-page">
    <StepProgress />
    
    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" md="8" lg="6">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h4 text-center pa-6 primary--text">
              Save
            </v-card-title>
            
            <v-card-text class="pa-6">
              <div class="mb-6" v-if="!((currentSaveStep === 'success' && memorialCreated) || (currentSaveStep === 'existing' && !hasChanges))">
                <p class="text-h6 mb-4" v-if="!store.isAccountCreated || !store.isEmailVerified">
                  Create your account to save {{ store.personName || 'this' }} memorial
                </p>
                <p class="text-h6 mb-4" v-else>
                  Save {{ store.personName || 'this' }} memorial
                </p>
                <p class="text-body-1 text--secondary mb-6" v-if="!store.isAccountCreated || !store.isEmailVerified">
                  We'll create a secure account for you to manage and share this memorial.
                </p>
                <p class="text-body-1 text--secondary mb-6" v-else>
                  Your memorial will be saved securely to your account.
                </p>
              </div>

              <!-- Logged-in User - Changes Status -->
              <v-expand-transition>
                <div v-if="currentSaveStep === 'existing'" class="save-step">
                  <v-card class="step-card" :class="hasChanges ? 'changes-card' : 'no-changes-card'" elevation="1">
                    <v-card-text class="text-center pa-6">
                      <v-icon size="64" :color="hasChanges ? 'warning' : 'success'" class="mb-4">
                        {{ hasChanges ? 'mdi-content-save-alert' : 'mdi-check-circle' }}
                      </v-icon>
                      <h3 class="text-h5 mb-4">
                        {{ hasChanges ? 'Unsaved Changes' : 'All Changes Saved' }}
                      </h3>
                      <p class="text-body-1 mb-4">
                        {{ hasChanges 
                          ? `You have unsaved changes to ${store.personName}'s memorial.` 
                          : `${store.personName}'s memorial is up to date.` 
                        }}
                      </p>
                      <p class="text-h6 primary--text mb-4">
                        {{ store.userEmail }}
                      </p>
                      
                      <v-alert v-if="apiError" type="error" variant="tonal" class="mb-4">
                        <div class="d-flex align-center justify-space-between">
                          <span>{{ apiError }}</span>
                          <v-btn
                            v-if="canRetryError"
                            color="error"
                            variant="text"
                            size="small"
                            @click="retryLastAction"
                          >
                            Try Again
                          </v-btn>
                        </div>
                      </v-alert>
                      
                      <p class="text-body-2 text--secondary" v-if="memorialCreated && !hasChanges">
                        Your memorial is saved and ready. You can proceed to payment to activate sharing features.
                      </p>
                      <p class="text-body-2 text--secondary" v-else-if="!apiError && hasChanges">
                        Click "Save Changes" to update your memorial with your recent changes.
                      </p>
                      <p class="text-body-2 text--secondary" v-else-if="!apiError && !hasChanges">
                        All your changes have been saved successfully.
                      </p>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>

              <!-- Email Entry Step with Sign In / Sign Up tabs -->
              <v-expand-transition>
                <div v-if="currentSaveStep === 'email'" class="save-step">
                  <v-card class="step-card" elevation="1">
                    <v-card-title class="step-header">
                      <v-icon left color="primary">mdi-account-circle-outline</v-icon>
                      Sign In or Create Account
                    </v-card-title>

                      <v-card-text>
                        <v-alert v-if="apiError" type="error" variant="tonal" class="mb-4">
                          {{ apiError }}
                        </v-alert>
                        <div v-if="authTab === 'signup' && showSignupResetHint" class="text-center mb-4">
                          <v-btn
                            variant="text"
                            color="primary"
                            size="small"
                            @click="goToPasswordReset"
                          >
                            Forgot your password?
                          </v-btn>
                        </div>

                      <v-tabs v-model="authTab" class="mt-2 mb-4 auth-tabs" color="primary">
                        <v-tab value="signup">Create Account</v-tab>
                        <v-tab value="signin">Sign In</v-tab>
                      </v-tabs>

                      <v-window v-model="authTab">
                        <!-- Sign Up Tab -->
                        <v-window-item value="signup">
                          <v-form ref="emailForm" v-model="emailValid">
                            <v-text-field
                              v-model="userName"
                              label="Your name"
                              variant="outlined"
                              density="comfortable"
                              :rules="nameRules"
                              required
                              autofocus
                              class="mb-3"
                            ></v-text-field>
                            <p class="text-caption text--secondary mb-4">
                              Add a name so your public contributions show your name instead of your email.
                            </p>

                            <v-text-field
                              v-model="userEmail"
                              label="Your email address"
                              type="email"
                              variant="outlined"
                              density="comfortable"
                              :rules="emailRules"
                              required
                            ></v-text-field>

                            <p class="text-caption text--secondary">
                              Enter your details to create your account.
                            </p>
                          </v-form>
                        </v-window-item>

                        <!-- Sign In Tab -->
                        <v-window-item value="signin">
                          <v-form ref="signinForm" v-model="signinValid" @submit.prevent="handleSignIn">
                            <v-text-field
                              v-model="signinEmail"
                              label="Email address"
                              type="email"
                              variant="outlined"
                              density="comfortable"
                              :rules="emailRules"
                              required
                              autofocus
                              class="mb-3"
                            ></v-text-field>

                            <v-text-field
                              v-model="signinPassword"
                              label="Password"
                              :type="showSigninPassword ? 'text' : 'password'"
                              variant="outlined"
                              density="comfortable"
                              :rules="passwordRules"
                              required
                              :append-inner-icon="showSigninPassword ? 'mdi-eye' : 'mdi-eye-off'"
                              @click:append-inner="showSigninPassword = !showSigninPassword"
                            ></v-text-field>
                            <div class="text-center mt-2">
                              <v-btn
                                variant="text"
                                color="primary"
                                size="small"
                                @click="goToPasswordReset"
                              >
                                Forgot your password?
                              </v-btn>
                            </div>
                          </v-form>
                        </v-window-item>
                      </v-window>
                    </v-card-text>

                    <v-card-actions class="pa-4">
                      <v-spacer></v-spacer>
                      <v-btn
                        v-if="authTab === 'signup'"
                        color="primary"
                        :disabled="!emailValid"
                        @click="proceedToPassword"
                      >
                        Continue
                      </v-btn>
                      <v-btn
                        v-else
                        color="primary"
                        :disabled="!signinValid || creating"
                        :loading="creating"
                        @click="handleSignIn"
                      >
                        Sign In
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </div>
              </v-expand-transition>

              <!-- Password Step -->
              <v-expand-transition>
                <div v-if="currentSaveStep === 'password'" class="save-step">
                  <v-card class="step-card" elevation="1">
                    <v-card-title class="step-header">
                      <v-icon left color="primary">mdi-lock-outline</v-icon>
                      Create Password
                    </v-card-title>
                    
                    <v-card-text>
                      <div class="mb-4" v-if="apiError">
                        <v-alert type="error" variant="tonal" class="mb-4">
                          <div class="d-flex align-center justify-space-between">
                            <span>{{ apiError }}</span>
                            <v-btn
                              v-if="canRetryError"
                              color="error"
                              variant="text"
                              size="small"
                              @click="retryLastAction"
                              :disabled="creating"
                            >
                              Try Again
                            </v-btn>
                          </div>
                        </v-alert>
                      </div>
                      
                      <v-alert
                        v-if="creating"
                        type="info"
                        variant="tonal"
                        density="compact"
                        class="mb-4"
                      >
                        <div class="d-flex align-center">
                          <v-progress-circular
                            indeterminate
                            size="20"
                            width="2"
                            color="primary"
                            class="mr-3"
                          ></v-progress-circular>
                          Creating your account...
                        </div>
                      </v-alert>

                      <v-form ref="passwordForm" v-model="passwordValid">
                        <v-text-field
                          v-model="userPassword"
                          label="Create password"
                          :type="showPassword ? 'text' : 'password'"
                          variant="outlined"
                          density="comfortable"
                          :rules="passwordRules"
                          required
                          autofocus
                          :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                          @click:append-inner="showPassword = !showPassword"
                          :disabled="creating"
                        ></v-text-field>

                        <v-text-field
                          v-model="confirmPassword"
                          label="Confirm password"
                          :type="showConfirmPassword ? 'text' : 'password'"
                          variant="outlined"
                          density="comfortable"
                          :rules="confirmPasswordRules"
                          required
                          class="mt-4"
                          :append-inner-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                          @click:append-inner="showConfirmPassword = !showConfirmPassword"
                          :disabled="creating"
                        ></v-text-field>

                        <p class="text-caption text--secondary mt-2">
                          Password must be at least 6 characters long.
                        </p>
                      </v-form>
                    </v-card-text>
                    
                    <v-card-actions class="pa-4">
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        :disabled="!passwordValid || creating"
                        :loading="creating"
                        @click="createAccount"
                      >
                        Create Account
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </div>
              </v-expand-transition>

              <!-- Verification Step -->
              <v-expand-transition>
                <div v-if="currentSaveStep === 'verify'" class="save-step">
                  <v-card class="step-card" elevation="1">
                    <v-card-title class="step-header">
                      <v-icon left color="primary">mdi-shield-check-outline</v-icon>
                      Verify Email
                    </v-card-title>
                    
                    <v-card-text>
                      <div class="mb-4" v-if="verificationCodeSent">
                        <p class="text-body-1 mb-2">
                          We've sent a verification code to:
                        </p>
                        <p class="text-h6 primary--text">{{ userEmail }}</p>
                      </div>
                      
                      <div class="mb-4" v-else>
                        <p class="text-body-1 mb-2">
                          Enter the verification code for:
                        </p>
                        <p class="text-h6 primary--text">{{ userEmail }}</p>
                      </div>
                      
                      <v-alert v-if="apiError" type="error" variant="tonal" class="mb-4">
                        <div class="d-flex align-center justify-space-between">
                          <span>{{ apiError }}</span>
                          <v-btn
                            v-if="canRetryError"
                            color="error"
                            variant="text"
                            size="small"
                            @click="retryLastAction"
                            :disabled="verifying || resending"
                          >
                            Try Again
                          </v-btn>
                        </div>
                      </v-alert>

                      <v-alert
                        v-if="verifying"
                        type="info"
                        variant="tonal"
                        density="compact"
                        class="mb-4"
                      >
                        <div class="d-flex align-center">
                          <v-progress-circular
                            indeterminate
                            size="20"
                            width="2"
                            color="primary"
                            class="mr-3"
                          ></v-progress-circular>
                          Verifying your email...
                        </div>
                      </v-alert>

                      <v-form ref="verifyForm" v-model="verifyValid">
                        <v-text-field
                          v-model="verificationCode"
                          label="Enter verification code"
                          variant="outlined"
                          density="comfortable"
                          :rules="codeRules"
                          required
                          autofocus
                          maxlength="6"
                          :disabled="verifying"
                        ></v-text-field>
                      </v-form>
                      
                      <div class="verification-actions mt-4">
                        <v-btn
                          variant="text"
                          color="secondary"
                          @click="resendCode"
                          :disabled="resendCooldown > 0 || resending"
                          :loading="resending"
                          size="small"
                        >
                          {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code' }}
                        </v-btn>
                        
                        <v-btn
                          variant="text"
                          color="secondary"
                          @click="goBackToPassword"
                          size="small"
                          class="ml-4"
                        >
                          Change Password
                        </v-btn>
                        
                        <v-btn
                          variant="text"
                          color="secondary"
                          @click="goBackToEmail"
                          size="small"
                          class="ml-4"
                        >
                          Change Email
                        </v-btn>
                      </div>
                    </v-card-text>
                    
                    <v-card-actions class="pa-4">
                      <v-spacer></v-spacer>
                      <v-btn
                        color="primary"
                        :disabled="!verifyValid || verifying"
                        :loading="verifying"
                        @click="verifyCode"
                      >
                        Verify Email
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </div>
              </v-expand-transition>

              <!-- Upload Progress Step -->
              <v-expand-transition>
                <div v-if="currentSaveStep === 'uploading'" class="save-step">
                  <v-card class="step-card" elevation="1">
                    <v-card-title class="step-header">
                      <v-icon left color="primary">mdi-cloud-upload-outline</v-icon>
                      Saving Memorial Content
                    </v-card-title>
                    
                    <v-card-text class="pa-6">
                      <div class="upload-progress">
                        <!-- Memorial Data Progress -->
                        <div class="progress-item mb-4">
                          <div class="d-flex align-center mb-2">
                            <v-icon
                              :color="uploadProgress.memorial.completed ? 'success' : (uploadProgress.memorial.inProgress ? 'primary' : 'grey')"
                              class="mr-2"
                              size="24"
                            >
                              {{ uploadProgress.memorial.completed ? 'mdi-check-circle' : (uploadProgress.memorial.inProgress ? 'mdi-loading mdi-spin' : 'mdi-circle-outline') }}
                            </v-icon>
                            <span class="text-body-1" :class="uploadProgress.memorial.inProgress ? 'font-weight-medium' : ''">
                              {{ uploadProgress.memorial.completed ? 'Memorial details saved' : (uploadProgress.memorial.inProgress ? 'Saving memorial details...' : 'Save memorial details') }}
                            </span>
                          </div>
                        </div>

                        <!-- Profile Photo Progress -->
                        <div v-if="store.profilePhotoFile" class="progress-item mb-4">
                          <div class="d-flex align-center mb-2">
                            <v-icon
                              :color="uploadProgress.profilePhoto.completed ? 'success' : (uploadProgress.profilePhoto.inProgress ? 'primary' : 'grey')"
                              class="mr-2"
                              size="24"
                            >
                              {{ uploadProgress.profilePhoto.completed ? 'mdi-check-circle' : (uploadProgress.profilePhoto.inProgress ? 'mdi-loading mdi-spin' : 'mdi-circle-outline') }}
                            </v-icon>
                            <span class="text-body-1" :class="uploadProgress.profilePhoto.inProgress ? 'font-weight-medium' : ''">
                              {{ uploadProgress.profilePhoto.completed ? 'Profile photo uploaded' : (uploadProgress.profilePhoto.inProgress ? 'Uploading profile photo...' : 'Upload profile photo') }}
                            </span>
                          </div>
                        </div>

                        <!-- Video Message Progress -->
                        <div v-if="store.videoMessage" class="progress-item mb-4">
                          <div class="d-flex align-center mb-2">
                            <v-icon
                              :color="uploadProgress.video.completed ? 'success' : (uploadProgress.video.inProgress ? 'primary' : 'grey')"
                              class="mr-2"
                              size="24"
                            >
                              {{ uploadProgress.video.completed ? 'mdi-check-circle' : (uploadProgress.video.inProgress ? 'mdi-loading mdi-spin' : 'mdi-circle-outline') }}
                            </v-icon>
                            <span class="text-body-1" :class="uploadProgress.video.inProgress ? 'font-weight-medium' : ''">
                              {{ uploadProgress.video.completed ? 'Video message uploaded' : (uploadProgress.video.inProgress ? 'Uploading video message...' : 'Upload video message') }}
                            </span>
                          </div>
                        </div>
                        
                        <!-- Photos Progress -->
                        <div v-if="store.photos.length > 0" class="progress-item mb-4">
                          <div class="d-flex align-center mb-2">
                            <v-icon
                              :color="uploadProgress.photos.completed ? 'success' : (uploadProgress.photos.inProgress ? 'primary' : 'grey')"
                              class="mr-2"
                              size="24"
                            >
                              {{ uploadProgress.photos.completed ? 'mdi-check-circle' : (uploadProgress.photos.inProgress ? 'mdi-loading mdi-spin' : 'mdi-circle-outline') }}
                            </v-icon>
                            <span class="text-body-1" :class="uploadProgress.photos.inProgress ? 'font-weight-medium' : ''">
                              {{ uploadProgress.photos.completed ? `All ${store.photos.length} photos uploaded` : (uploadProgress.photos.inProgress ? `Uploading photos (${uploadProgress.photos.current}/${store.photos.length})...` : `Upload ${store.photos.length} photo${store.photos.length > 1 ? 's' : ''}`) }}
                            </span>
                          </div>
                          <v-progress-linear
                            v-if="uploadProgress.photos.inProgress || uploadProgress.photos.completed"
                            :model-value="(uploadProgress.photos.current / store.photos.length) * 100"
                            color="primary"
                            height="6"
                            rounded
                            class="ml-8"
                          ></v-progress-linear>
                        </div>

                        <!-- Voice memories progress -->
                        <div v-if="store.audios.length > 0" class="progress-item mb-4">
                          <div class="d-flex align-center mb-2">
                            <v-icon
                              :color="uploadProgress.audios.completed ? 'success' : (uploadProgress.audios.inProgress ? 'primary' : 'grey')"
                              class="mr-2"
                              size="24"
                            >
                              {{ uploadProgress.audios.completed ? 'mdi-check-circle' : (uploadProgress.audios.inProgress ? 'mdi-loading mdi-spin' : 'mdi-circle-outline') }}
                            </v-icon>
                            <span class="text-body-1">
                              {{ uploadProgress.audios.completed ? 'Voice memories uploaded' : (uploadProgress.audios.inProgress ? `Uploading voice memories (${uploadProgress.audios.current}/${store.audios.length})...` : 'Upload voice memories') }}
                            </span>
                          </div>
                        </div>

                        <!-- Overall Progress Summary -->
                        <v-divider class="my-4"></v-divider>

                        <v-alert
                          type="info"
                          variant="tonal"
                          density="comfortable"
                          class="mt-4"
                        >
                          <div class="d-flex align-center">
                            <v-progress-circular
                              v-if="isUploadInProgress"
                              indeterminate
                              size="24"
                              width="3"
                              color="primary"
                              class="mr-3"
                            ></v-progress-circular>
                            <v-icon v-else size="24" color="success" class="mr-3">mdi-check-circle</v-icon>
                            <div>
                              <div class="text-body-1 font-weight-medium">
                                {{ uploadProgress.currentStep || 'Preparing upload...' }}
                              </div>
                              <div class="text-caption text--secondary mt-1">
                                Please don't close this window while we save your memorial
                              </div>
                            </div>
                          </div>
                        </v-alert>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>

              <!-- Success Step -->
              <v-expand-transition>
                <div v-if="currentSaveStep === 'success' && memorialCreated" class="save-step">
                  <v-card class="step-card success-card" elevation="0">
                    <v-card-text class="text-center pa-6">
                      <v-icon size="64" color="success" class="mb-4">
                        mdi-check-circle
                      </v-icon>
                      <h3 class="text-h5 mb-4">Memorial Saved Successfully!</h3>
                      <p class="text-body-1 text--secondary mb-4">
                        Your memorial for {{ store.personName }} has been saved with all content uploaded.
                      </p>
                      <p class="text-body-2 text--secondary">
                        You can now proceed to payment to activate sharing features.
                      </p>
                    </v-card-text>
                  </v-card>
                </div>
              </v-expand-transition>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="previousStep"
                :disabled="currentSaveStep === 'verify' || currentSaveStep === 'uploading'"
                class="mr-4"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              
              <v-spacer></v-spacer>
              
              <!-- Save Changes button for logged-in users with changes -->
              <v-btn
                v-if="currentSaveStep === 'existing' && hasChanges && !apiError"
                color="warning"
                size="large"
                rounded="pill"
                @click="saveChanges"
                :loading="creating"
                :disabled="creating"
                class="px-8 mr-4"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save Changes
              </v-btn>
              
              <!-- Continue to Payment button -->
              <v-btn
                v-if="(currentSaveStep === 'success' && memorialCreated) || (currentSaveStep === 'existing' && !hasChanges)"
                color="primary"
                size="large"
                rounded="pill"
                @click="nextStep"
                class="px-8"
              >
                Continue to Payment
                <v-icon right class="ml-2">mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import StepProgress from '../components/StepProgress.vue'
import apiService from '../services/api'
import authService from '../services/auth'

const router = useRouter()
const store = useMemorialStore()

// Form refs
const emailForm = ref(null)
const verifyForm = ref(null)
const passwordForm = ref(null)

// Form validation
const emailValid = ref(false)
const verifyValid = ref(false)
const passwordValid = ref(false)

// Form data
const userName = ref('')
const userEmail = ref('')
const verificationCode = ref('')
const userPassword = ref('')
const confirmPassword = ref('')

// Sign in data
const authTab = ref('signup')
const signinEmail = ref('')
const signinPassword = ref('')
const signinValid = ref(false)
const signinForm = ref(null)
const showSigninPassword = ref(false)

// UI state
const currentSaveStep = ref('email')
// Use computed to reactively track changes
const hasChanges = computed(() => store.hasUnsavedChangesComputed)

const isUploadInProgress = computed(() => {
  const memorial = !uploadProgress.value.memorial.completed
  const profilePhoto = store.profilePhotoFile && !uploadProgress.value.profilePhoto.completed
  const video = store.videoMessage && !uploadProgress.value.video.completed
  const photos = store.photos.length > 0 && !uploadProgress.value.photos.completed
  const audios = store.audios.length > 0 && !uploadProgress.value.audios.completed
  return memorial || profilePhoto || video || photos || audios
})
const verifying = ref(false)
const creating = ref(false)
const resending = ref(false)
const resendCooldown = ref(0)
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const apiError = ref('')
const canRetryError = ref(false)
const lastFailedAction = ref(null)
const verificationCodeSent = ref(false)
const memorialCreated = ref(false)
const signupToken = ref('')
const showSignupResetHint = ref(false)

// Upload progress tracking
const uploadProgress = ref({
  memorial: {
    inProgress: false,
    completed: false
  },
  video: {
    inProgress: false,
    completed: false
  },
  photos: {
    inProgress: false,
    completed: false,
    current: 0
  },
  audios: {
    inProgress: false,
    completed: false,
    current: 0
  },
  currentStep: ''
})

// Validation rules
const nameRules = [
  v => !!v || 'Name is required'
]

const emailRules = [
  v => !!v || 'Email is required',
  v => /.+@.+\..+/.test(v) || 'Email must be valid'
]

const codeRules = [
  v => !!v || 'Verification code is required',
  v => v.length === 5 || 'Code must be 5 characters'
]

const passwordRules = [
  v => !!v || 'Password is required',
  v => v.length >= 6 || 'Password must be at least 6 characters'
]

const confirmPasswordRules = [
  v => !!v || 'Please confirm your password',
  v => v === userPassword.value || 'Passwords do not match'
]

// Resend cooldown timer
let resendTimer = null

const resetSignupFlowToEmail = (message) => {
  if (resendTimer) {
    clearInterval(resendTimer)
    resendTimer = null
  }

  resendCooldown.value = 0
  verificationCodeSent.value = false
  signupToken.value = ''
  verificationCode.value = ''
  userPassword.value = ''
  confirmPassword.value = ''
  currentSaveStep.value = 'email'
  apiError.value = message || 'We couldn\'t create your account right now. Please try again.'
  showSignupResetHint.value = /password|already|exists|registered|in use/i.test(apiError.value)
  canRetryError.value = false
  lastFailedAction.value = null
}

// Methods
const proceedToPassword = () => {
  if (!emailValid.value) return
  apiError.value = ''
  canRetryError.value = false
  currentSaveStep.value = 'password'
}

const handleSignIn = async () => {
  if (!signinValid.value) return

  creating.value = true
  apiError.value = ''
  canRetryError.value = false

  try {
    const result = await authService.login(signinEmail.value, signinPassword.value)

    if (result.success) {
      // User authenticated successfully
      store.setApiAuth(result.response.token, result.response.user)

      // Load their memorial if they have one
      if (result.response.memorial) {
        store.loadMemorialData(result.response.memorial)
        // Go to payment page if not yet paid
        const isPaid = typeof result.response.memorial.paymentCompleted === 'boolean'
          ? result.response.memorial.paymentCompleted
          : Boolean(result.response.memorial.stripeCustomerId)

        if (!isPaid) {
          store.goToStep(6) // Payment step
          router.push('/payment')
        } else {
          router.push('/memory/dashboard')
        }
      } else {
        // No existing memorial, proceed with saving this one
        currentSaveStep.value = 'uploading'
        await createMemorial()
        memorialCreated.value = true
      }
    } else {
      apiError.value = result.error || 'Sign in failed. Please check your credentials.'
      canRetryError.value = true
    }
  } catch (err) {
    console.error('Sign in error:', err)
    apiError.value = 'An error occurred during sign in. Please try again.'
    canRetryError.value = true
  } finally {
    creating.value = false
  }
}

const verifyCode = async () => {
  if (!verifyValid.value) return
  
  verifying.value = true
  apiError.value = ''
  canRetryError.value = false
  lastFailedAction.value = null
  
  try {
    const response = await apiService.confirmCode(verificationCode.value, signupToken.value)
    
    if (response.success) {
      // Now mark the account as fully created and verified
      store.verifyEmail(verificationCode.value)
      store.markAccountCreated()

      verifying.value = false

      // Give the server time to finalize the account before logging in
      await new Promise(resolve => setTimeout(resolve, 500))

      const loggedIn = await loginAfterVerify()
      if (!loggedIn) {
        return
      }

      // Create the memorial and wait for completion before showing success
      try {
        await createMemorial()
        memorialCreated.value = true
        currentSaveStep.value = 'success'
      } catch (error) {
        console.error('Memorial creation failed after successful account verification:', error)
        const errorInfo = apiService.formatErrorMessage(error)
        apiError.value = `Your account is ready, but we couldn't save your memorial. ${errorInfo.message}`
        canRetryError.value = true
        lastFailedAction.value = 'createMemorial'
        // Stay on verify step so user can retry memorial creation
      }
    } else {
      throw new Error(response.msg || 'Verification failed')
    }
  } catch (error) {
    verifying.value = false
    const errorInfo = apiService.formatErrorMessage(error)
    apiError.value = errorInfo.message
    canRetryError.value = errorInfo.canRetry
    lastFailedAction.value = 'verifyCode'
    console.error('Verification error:', error)
  }
}

const loginAfterVerify = async () => {
  try {
    const loginResult = await authService.login(userEmail.value, userPassword.value)
    if (!loginResult.success) {
      apiError.value = loginResult.error || 'Account verified, but sign in failed. Please try again.'
      canRetryError.value = true
      lastFailedAction.value = 'loginAfterVerify'
      return false
    }

    signupToken.value = loginResult.response.token
    return true
  } catch (error) {
    console.error('Post-verify login error:', error)
    const errorInfo = apiService.formatErrorMessage(error)
    apiError.value = errorInfo.message
    canRetryError.value = errorInfo.canRetry
    lastFailedAction.value = 'loginAfterVerify'
    return false
  }
}

const createAccount = async () => {
  if (!passwordValid.value) return
  
  creating.value = true
  apiError.value = ''
  showSignupResetHint.value = false
  canRetryError.value = false
  lastFailedAction.value = null
  
  try {
    // Call real API to create account and send verification email
    console.log('Creating account for:', userEmail.value)
    const response = await apiService.signUp(userEmail.value, userPassword.value, null, userName.value)
    console.log('Account creation response:', response)
    
    if (response.success) {
      // Store the signup token for verification API calls
      signupToken.value = response.token

      // Store user data from signup response for memorial creation
      if (response.user) {
        store.setApiAuth(response.token, response.user)
      } else {
        // Even without full user object, store basic auth info
        store.updateUserEmail(userEmail.value)
        store.updateUserPassword(userPassword.value)
        // Set API token in the API service
        apiService.setToken(response.token)
      }

      creating.value = false
      verificationCodeSent.value = true
      currentSaveStep.value = 'verify'

      // Start resend cooldown only after successful account creation
      startResendCooldown()
    } else if (response.pendingVerification && response.token) {
      // Account exists but is not verified - show verification step
      signupToken.value = response.token

      // Store basic auth info
      store.updateUserEmail(userEmail.value)
      store.updateUserPassword(userPassword.value)
      apiService.setToken(response.token)

      creating.value = false
      verificationCodeSent.value = true
      currentSaveStep.value = 'verify'

      // Start resend cooldown
      startResendCooldown()
    } else {
      creating.value = false
      resetSignupFlowToEmail(response.msg || 'We couldn\'t create your account right now. Please try again.')
      return
    }
  } catch (error) {
    creating.value = false
    const errorInfo = apiService.formatErrorMessage(error)
    resetSignupFlowToEmail(errorInfo.message)
    console.error('Account creation error:', error)
  }
}

const resendCode = async () => {
  if (resendCooldown.value > 0 || resending.value) return
  
  resending.value = true
  apiError.value = ''
  canRetryError.value = false
  lastFailedAction.value = null
  
  try {
    const response = await apiService.resendCode(signupToken.value)
    
    if (response.success) {
      resending.value = false
      verificationCodeSent.value = true
      // Only start cooldown if the API request succeeded
      startResendCooldown()
    } else {
      throw new Error(response.msg || 'Failed to resend code')
    }
  } catch (error) {
    resending.value = false
    const errorInfo = apiService.formatErrorMessage(error)
    apiError.value = errorInfo.message
    canRetryError.value = errorInfo.canRetry
    lastFailedAction.value = 'resendCode'
    console.error('Resend code error:', error)
  }
}

const createMemorial = async () => {
  try {
    // Use existing API token for logged-in users, or signup token for new users
    const authToken = store.apiToken || signupToken.value

    if (!authToken) {
      throw new Error('Authentication token is missing. Please refresh the page and try again.')
    }

    // Show upload progress step
    currentSaveStep.value = 'uploading'
    
    // Reset progress tracking
    uploadProgress.value = {
      memorial: { inProgress: false, completed: false },
      profilePhoto: { inProgress: false, completed: false },
      video: { inProgress: false, completed: false },
      photos: { inProgress: false, completed: false, current: 0 },
      audios: { inProgress: false, completed: false, current: 0 },
      currentStep: ''
    }
    
    // Step 1: Save memorial data
    uploadProgress.value.memorial.inProgress = true
    uploadProgress.value.currentStep = 'Saving memorial details...'

    const reachedStep = Math.max(store.maxReachedStep, store.currentStep || 0)
    store.maxReachedStep = reachedStep
    
    const memorialData = {
      uuid: null, // null for creation
      userUuid: store.user?.uuid || null,
      adminUuids: null,
      nameOfPerson: store.personName || null,
      birthDate: store.birthDate || '',
      deathDate: store.deathDate || '',
      hasEvent: store.hasEvent,
      eventName: store.eventName || '',
      memorialDate: store.memorialDate || null,
      address: store.memorialAddress || null,
      details: store.memorialDetails || null,
      mediaUuids: null, // Will be updated after uploads
      stories: buildStoriesArray(),
      contacts: buildContactsArray(),
      hostMessage: store.personalMessage || null,
      hostVideoMessageUuid: null, // Will be updated after video upload
      stripeCustomerId: null,
      shareSlug: store.ensureShareSlug(),
      maxReachedStep: reachedStep
    }

    console.log('Creating memorial with data:', memorialData)
    
    const response = await apiService.createMemorial(memorialData, authToken)
    
    if (!response.success) {
      throw new Error(response.msg || 'We couldn\'t save your memorial. Please try again.')
    }
    
    // Store the memorial UUID from the response
    const memorialUuid = response.memorial?.uuid
    if (memorialUuid) {
      store.setMemorialUuid(memorialUuid)
    } else {
      throw new Error('No memorial UUID returned from server')
    }

    if (response.memorial?.shareSlug) {
      store.setShareSlug(response.memorial.shareSlug)
    }
    
    uploadProgress.value.memorial.completed = true
    uploadProgress.value.memorial.inProgress = false

    console.log('Memorial created successfully:', response)

    // Step 2: Upload profile photo if present
    if (store.profilePhotoFile) {
      uploadProgress.value.profilePhoto.inProgress = true
      uploadProgress.value.currentStep = 'Uploading profile photo...'

      try {
        const profilePhotoResponse = await apiService.uploadPhoto(
          memorialUuid,
          store.profilePhotoFile,
          '', // No caption for profile photo
          authToken,
          {
            isProfilePhoto: true
          }
        )

        if (!profilePhotoResponse.success) {
          throw new Error(profilePhotoResponse.msg || 'Failed to upload profile photo')
        }

        uploadProgress.value.profilePhoto.completed = true
        uploadProgress.value.profilePhoto.inProgress = false
        console.log('Profile photo uploaded successfully:', profilePhotoResponse)
      } catch (profilePhotoError) {
        console.error('Profile photo upload error:', profilePhotoError)
        uploadProgress.value.profilePhoto.inProgress = false
        // Continue with other uploads even if profile photo fails
      }
    }

    // Step 3: Upload video message if present
    if (store.videoMessage) {
      uploadProgress.value.video.inProgress = true
      uploadProgress.value.currentStep = 'Uploading video message...'
      
      try {
        const videoResponse = await apiService.uploadVideoMessage(
          memorialUuid, 
          store.videoMessage, 
          '',
          authToken
        )
        
        if (!videoResponse.success) {
          throw new Error(videoResponse.msg || 'Failed to upload video message')
        }
        
        uploadProgress.value.video.completed = true
        uploadProgress.value.video.inProgress = false
        console.log('Video message uploaded successfully:', videoResponse)
      } catch (videoError) {
        console.error('Video upload error:', videoError)
        uploadProgress.value.video.inProgress = false
        // Continue with photo uploads even if video fails
      }
    }
    
    // Step 4: Upload photos if present
    if (store.photos.length > 0) {
      uploadProgress.value.photos.inProgress = true
      uploadProgress.value.currentStep = 'Uploading photos...'
      
      for (let i = 0; i < store.photos.length; i++) {
        const photo = store.photos[i]
        uploadProgress.value.photos.current = i
        uploadProgress.value.currentStep = `Uploading photo ${i + 1} of ${store.photos.length}...`
        
        try {
          const photoResponse = await apiService.uploadPhoto(
            memorialUuid,
            photo.file,
            photo.caption || '',
            authToken
          )
          
          if (!photoResponse.success) {
            console.error(`Failed to upload photo ${i + 1}:`, photoResponse.msg)
            // Continue with next photo
            continue
          }
          
          console.log(`Photo ${i + 1} uploaded successfully:`, photoResponse)
        } catch (photoError) {
          console.error(`Photo ${i + 1} upload error:`, photoError)
          // Continue with next photo
        }
      }
      
      uploadProgress.value.photos.current = store.photos.length
      uploadProgress.value.photos.completed = true
      uploadProgress.value.photos.inProgress = false
    }

    if (store.audios.length > 0) {
      uploadProgress.value.audios.inProgress = true
      for (let i = 0; i < store.audios.length; i++) {
        const audio = store.audios[i]
        uploadProgress.value.audios.current = i
        uploadProgress.value.currentStep = `Uploading voice memory ${i + 1} of ${store.audios.length}...`
        if (!audio.file) continue

        const audioResponse = await apiService.uploadAudio(
          memorialUuid,
          audio.file,
          audio.caption || '',
          authToken
        )
        if (!audioResponse.success) {
          throw new Error(audioResponse.msg || `Failed to upload ${audio.name}`)
        }
        audio.id = audioResponse.media?.uuid
        audio.file = null
        audio.serverData = audioResponse.media
      }
      uploadProgress.value.audios.current = store.audios.length
      uploadProgress.value.audios.completed = true
      uploadProgress.value.audios.inProgress = false
    }
    
    // All uploads complete
    uploadProgress.value.currentStep = 'Upload complete!'
    
    // Short delay to show completion, then move to success
    setTimeout(() => {
      currentSaveStep.value = 'success'
    }, 1500)
    
  } catch (error) {
    console.error('Memorial creation error:', error)
    // Show error and revert to previous step
    const errorInfo = apiService.formatErrorMessage(error)
    apiError.value = `We couldn't save your memorial right now. ${errorInfo.message}`
    currentSaveStep.value = 'verify'
    throw error // Re-throw so the calling function can handle it
  }
}

const buildStoriesArray = () => {
  return store.stories
    .filter(story => story && story.trim())
    .map(story => ({
      uuid: null,
      text: story.trim()
    }))
}

const buildContactsArray = () => {
  console.log('buildContactsArray - store.contacts:', store.contacts)
  const result = store.contacts
    .filter(contact => contact && contact.trim())
    .map(contact => {
      const contactData = {
        uuid: null,
        email: null,
        phone: null,
        firstName: null,
        surname: null
      }

      // Check if it's an email or phone
      if (/.+@.+\..+/.test(contact.trim())) {
        contactData.email = contact.trim()
      } else if (/^[+]?[\s\-()]*([0-9][\s\-()]*){7,}/.test(contact.trim())) {
        contactData.phone = contact.trim()
      }

      return contactData
    })
    .filter(contact => contact.email || contact.phone) // Only include valid contacts
  console.log('buildContactsArray - result:', result)
  return result
}

const goBackToPassword = () => {
  currentSaveStep.value = 'password'
  verificationCode.value = ''
  clearResendTimer()
}

const goBackToEmail = () => {
  currentSaveStep.value = 'email'
  verificationCode.value = ''
  userPassword.value = ''
  confirmPassword.value = ''
  clearResendTimer()
  apiError.value = ''
  canRetryError.value = false
  lastFailedAction.value = null
}

const startResendCooldown = () => {
  resendCooldown.value = 30
  resendTimer = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0) {
      clearResendTimer()
    }
  }, 1000)
}

const goToPasswordReset = () => {
  router.push({ path: '/login', query: { reset: '1' } })
}

const clearResendTimer = () => {
  if (resendTimer) {
    clearInterval(resendTimer)
    resendTimer = null
  }
  resendCooldown.value = 0
}

const retryLastAction = async () => {
  if (!lastFailedAction.value) return
  
  apiError.value = ''
  canRetryError.value = false
  
  switch (lastFailedAction.value) {
    case 'createAccount':
      await createAccount()
      break
    case 'verifyCode':
      await verifyCode()
      break
    case 'loginAfterVerify': {
      const loggedIn = await loginAfterVerify()
      if (!loggedIn) break
      try {
        await createMemorial()
        memorialCreated.value = true
        currentSaveStep.value = 'success'
        lastFailedAction.value = null
      } catch (error) {
        console.error('Memorial creation retry failed after login:', error)
        const errorInfo = apiService.formatErrorMessage(error)
        apiError.value = `We still couldn't save your memorial. ${errorInfo.message}`
        canRetryError.value = errorInfo.canRetry
        lastFailedAction.value = 'createMemorial'
      }
      break
    }
    case 'resendCode':
      await resendCode()
      break
    case 'createMemorial':
      try {
        await createMemorial()
        memorialCreated.value = true
        currentSaveStep.value = 'success'
        lastFailedAction.value = null
      } catch (error) {
        console.error('Memorial creation retry failed:', error)
        const errorInfo = apiService.formatErrorMessage(error)
        apiError.value = `We still couldn't save your memorial. ${errorInfo.message}`
        canRetryError.value = errorInfo.canRetry
        lastFailedAction.value = 'createMemorial'
      }
      break
    case 'saveChanges':
      await saveChanges()
      break
    case 'loadMemorial':
      // Retry loading memorial data
      if (store.memorialUuid) {
        store.loadUserMemorialFromServer().then(loaded => {
          if (loaded) {
            store.initializeForLoggedInUser()
            checkForChanges()
            memorialCreated.value = true
            apiError.value = ''
            lastFailedAction.value = null
          } else {
            apiError.value = 'Still unable to load memorial data. Please contact support.'
            canRetryError.value = false
          }
        })
      }
      break
    default:
      console.warn('Unknown action to retry:', lastFailedAction.value)
  }
}

// Navigation
const nextStep = () => {
  store.goToStep(6) // Payment step
  router.push('/payment')
}

const previousStep = () => {
  store.goToStep(4) // Preview step
  router.push('/preview')
}

// Lifecycle
onMounted(() => {
  userEmail.value = store.userEmail
  
  // Set up API token if available
  if (store.apiToken) {
    apiService.setToken(store.apiToken)
  }
  
  // Check if user is already logged in (has API token and verified account)
  if (store.isAccountCreated && store.isEmailVerified && store.apiToken) {
    currentSaveStep.value = 'existing'
    
    // For logged-in users, we need to handle different scenarios
    if (store.memorialUuid) {
      // User has a memorial UUID, try to load data from server
      store.loadUserMemorialFromServer().then(loaded => {
        if (loaded) {
          // Successfully loaded memorial data from server
          store.initializeForLoggedInUser() // Set baseline
          memorialCreated.value = true
        } else {
          // Failed to load from server, but user has local data
          if (store.personName || store.memorialAddress || store.stories.length > 1 || store.contacts.length > 0) {
            // User has local changes, treat as unsaved
            store.initializeForLoggedInUser()
            memorialCreated.value = true
          } else {
            // No data available, show error
            apiError.value = 'Unable to load your memorial data. Please try refreshing the page.'
            canRetryError.value = true
            lastFailedAction.value = 'loadMemorial'
          }
        }
      }).catch(error => {
        console.error('Error loading memorial for logged-in user:', error)
        // Fall back to local data if available
        if (store.personName || store.memorialAddress) {
          store.initializeForLoggedInUser()
          memorialCreated.value = true
        } else {
          apiError.value = 'Unable to load your memorial data. Please try refreshing the page.'
          canRetryError.value = true
          lastFailedAction.value = 'loadMemorial'
        }
      })
    } else {
      // No memorial UUID - this is a first-time save for logged-in user
      if (store.personName || store.memorialAddress || store.stories.some(s => s.trim()) || store.contacts.length > 0) {
        // User has form data to save
        createMemorial().then(() => {
          memorialCreated.value = true
          store.initializeForLoggedInUser() // Set baseline after creation
        }).catch(error => {
          console.error('Memorial creation failed for logged-in user:', error)
          const errorInfo = apiService.formatErrorMessage(error)
          apiError.value = `Memorial creation failed: ${errorInfo.message}`
          canRetryError.value = errorInfo.canRetry
          lastFailedAction.value = 'createMemorial'
        })
      } else {
        // No data to save yet
        store.initializeForLoggedInUser()
        memorialCreated.value = false
      }
    }
  } else {
    // New user needs to go through account creation flow
    currentSaveStep.value = 'email'
  }

  store.goToStep(5) // Set current step to Save
})

// Save changes for logged-in users
const saveChanges = async () => {
  if (!hasChanges.value) return
  
  creating.value = true
  apiError.value = ''
  canRetryError.value = false
  lastFailedAction.value = null
  
  try {
    // Update existing memorial
    await updateMemorial()
    
    // Mark changes as saved
    store.markChangesSaved()
    memorialCreated.value = true
    
    creating.value = false
  } catch (error) {
    creating.value = false
    const errorInfo = apiService.formatErrorMessage(error)
    apiError.value = `Failed to save changes: ${errorInfo.message}`
    canRetryError.value = errorInfo.canRetry
    lastFailedAction.value = 'saveChanges'
    console.error('Save changes error:', error)
  }
}

// Check for changes (now reactive through computed)
const checkForChanges = () => {
  // This function is kept for backward compatibility but the real work
  // is now done by the reactive computed property hasChanges
  console.log('Changes detected:', hasChanges.value)
}

// Update memorial (for logged-in users)
const updateMemorial = async () => {
  const authToken = store.apiToken
  
  if (!authToken) {
    throw new Error('Authentication token is missing. Please refresh the page and try again.')
  }

  const memorialData = {
    uuid: store.memorialUuid,
    userUuid: store.user?.uuid || null,
    adminUuids: null,
    nameOfPerson: store.personName || null,
    birthDate: store.birthDate || '',
    deathDate: store.deathDate || '',
    hasEvent: store.hasEvent,
    eventName: store.eventName || '',
    memorialDate: store.memorialDate || null,
    address: store.memorialAddress || null,
    details: store.memorialDetails || null,
    mediaUuids: null,
    stories: buildStoriesArray(),
    contacts: buildContactsArray(),
    hostMessage: store.personalMessage || null,
    hostVideoMessageUuid: null,
    stripeCustomerId: null,
    shareSlug: store.ensureShareSlug(),
    maxReachedStep: store.maxReachedStep
  }

  console.log('Updating memorial with data:', memorialData)
  
  const response = await apiService.editMemorial(memorialData, authToken)
  
  if (!response.success) {
    throw new Error(response.msg || 'Failed to update memorial. Please try again.')
  }
  
  console.log('Memorial updated successfully:', response)

  if (response.memorial?.shareSlug) {
    store.setShareSlug(response.memorial.shareSlug)
  }
  
  // Handle any new media uploads if needed
  const memorialUuid = store.memorialUuid
  
  // Upload video message if changed
  if (store.videoMessage && store.originalMemorialData?.videoMessage !== store.videoMessage) {
    try {
      const videoResponse = await apiService.uploadVideoMessage(
        memorialUuid,
        store.videoMessage,
        '',
        authToken
      )
      
      if (!videoResponse.success) {
        console.error('Failed to upload video message:', videoResponse.msg)
      } else {
        console.log('Video message updated successfully:', videoResponse)
      }
    } catch (videoError) {
      console.error('Video upload error:', videoError)
    }
  }
  
  // Upload new photos if any
  const newPhotos = store.photos.filter(photo => 
    !store.originalMemorialData?.photos.find(orig => orig.id === photo.id)
  )
  
  for (let i = 0; i < newPhotos.length; i++) {
    const photo = newPhotos[i]
    try {
      const photoResponse = await apiService.uploadPhoto(
        memorialUuid,
        photo.file,
        photo.caption || '',
        authToken
      )
      
      if (!photoResponse.success) {
        console.error(`Failed to upload new photo ${i + 1}:`, photoResponse.msg)
      } else {
        console.log(`New photo ${i + 1} uploaded successfully:`, photoResponse)
      }
    } catch (photoError) {
      console.error(`New photo ${i + 1} upload error:`, photoError)
    }
  }

  const newAudios = store.audios.filter(audio => audio.file)
  for (const audio of newAudios) {
    const response = await apiService.uploadAudio(memorialUuid, audio.file, audio.caption || '', authToken)
    if (!response.success) {
      throw new Error(response.msg || `Failed to upload ${audio.name}`)
    }
    audio.id = response.media?.uuid
    audio.file = null
    audio.serverData = response.media
  }
}

onUnmounted(() => {
  clearResendTimer()
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;
.save-page {
  @include page-container;
}

.main-card {
  @include main-card;
  
  .v-card-title {
    @include section-header;
  }
}

.save-step {
  width: 100%;
}

.step-card {
  @include card-base;
  
  .step-header {
    @include section-header-light;
  }
}

.auth-tabs {
  border: $border-width solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  overflow: hidden;
  background: var(--v-theme-surface);

  :deep(.v-tab) {
    border-radius: 0;
    border: none !important;
    min-height: 40px;
    text-transform: none;
  }

  :deep(.v-tab:not(:last-child)) {
    border-right: $border-width solid rgba(15, 23, 42, 0.12) !important;
  }
}

.success-card {
  border: $border-width solid $success;
  background: $white;
}

.changes-card {
  border: $border-width solid $warning;
  background: rgba($warning, 0.1);
}

.no-changes-card {
  border: $border-width solid $success;
  background: rgba($success, 0.05);
}

.verification-actions {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.upload-progress {
  .progress-item {
    .mdi-loading {
      animation: spin 1s linear infinite;
    }
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@include mobile {
  .pa-6 {
    padding: 1rem !important;
  }
  
  .verification-actions {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    
    .ml-4 {
      margin-left: 0 !important;
    }
  }
}
</style>
